<script setup lang="ts">
import CollapsibleSearchPanel from '@/components/Globals/CollapsibleSearchPanel.vue'
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'
import SearchBarField from '@/components/Globals/SearchBarField.vue'
import type { IDynamicFilterItem, IFilterFieldConfig, FilterOperator } from '@/interfaces/components/searchBar/dynamicFilter'
import type { IAlarmListingDynamicFilter } from '@/store/alarm'
import { useAlarmStore } from '@/store/alarm'
import {
  formatAlarmListingFilterDateForChip,
  isAlarmListingDateField,
  isValidAlarmListingFilterDateValue,
  normalizeAlarmListingDynamicFilterValue,
  normalizeAlarmListingFilterDateInput,
  resolveAlarmListingFilterValueType,
} from '@/utils/alarm-listing/filter-date'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const { t } = useI18n()
const alarmStore = useAlarmStore()
const { setCurrentClassificationStatusFilter } = alarmStore

const listingState = alarmStore.getListingTableState

const fieldInputClass =
  'w-full rounded-none border border-stroke bg-white px-3 py-2 text-sm text-gray-900 transition-colors hover:border-gray-400 focus:border-primary focus:outline-none'

const panelControlClass =
  '[&_input]:rounded-none [&_button]:rounded-none [&_select]:rounded-none'

const filterFields = computed<IFilterFieldConfig[]>(() => [
  { key: 'id', label: t('alarm_listing.filters.fields.id'), valueType: 'number', operators: ['eq', 'gt', 'lt'] },
  { key: 'name', label: t('alarm_listing.filters.fields.name'), valueType: 'string', operators: ['cont', 'eq'] },
  { key: 'alarm_type', label: t('alarm_listing.filters.fields.alarm_type'), valueType: 'string', operators: ['eq', 'cont'] },
  { key: 'sensor_name', label: t('alarm_listing.filters.fields.sensor_name'), valueType: 'string', operators: ['cont', 'eq'] },
  { key: 'group_name', label: t('alarm_listing.filters.fields.group_name'), valueType: 'string', operators: ['cont', 'eq'] },
  {
    key: 'classification_type_name',
    label: t('alarm_listing.filters.fields.classification_type_name'),
    valueType: 'string',
    operators: ['cont', 'eq'],
  },
  { key: 'start_date', label: t('alarm_listing.filters.fields.start_date'), valueType: 'date', operators: ['gteq', 'lteq'] },
  { key: 'end_date', label: t('alarm_listing.filters.fields.end_date'), valueType: 'date', operators: ['gteq', 'lteq'] },
])

const listClassifications = computed(() => [
  { id: true, name: t('alarm_listing.filters.classification_classified') },
  { id: false, name: t('alarm_listing.filters.classification_unclassified') },
])

function normalizeDraftFilter(item: IAlarmListingDynamicFilter): IDynamicFilterItem {
  const valueType = resolveAlarmListingFilterValueType(item.field, item.valueType)
  return {
    ...item,
    valueType,
    value: normalizeAlarmListingDynamicFilterValue(item.field, item.value, valueType),
  }
}

const draftFilters = ref<IDynamicFilterItem[]>(
  Array.isArray(listingState.dynamicFilters)
    ? listingState.dynamicFilters.map((item) => normalizeDraftFilter({ ...item }))
    : [],
)

const draftField = ref(filterFields.value[0]?.key ?? 'name')
const draftOperator = ref<FilterOperator>(filterFields.value[0]?.operators[0] ?? 'cont')
const draftValue = ref('')
const draftValueType = computed(() => getFieldConfig(draftField.value).valueType)

function serializeFiltersForComparison(filters: Array<IDynamicFilterItem | IAlarmListingDynamicFilter>): string {
  return filters
    .filter((f) => isValidAppliedFilter(f as IDynamicFilterItem))
    .map((f) => {
      const normalized = normalizeDraftFilter(f as IAlarmListingDynamicFilter)
      return `${normalized.field}|${normalized.operator}|${String(normalized.value)}|${normalized.valueType}`
    })
    .sort()
    .join(';;')
}

const canApplyFilters = computed(() => {
  const draftSnapshot = serializeFiltersForComparison(draftFilters.value)
  const appliedSnapshot = serializeFiltersForComparison(alarmStore.getListingTableState.dynamicFilters || [])
  return draftSnapshot !== appliedSnapshot
})

const canClearFilters = computed(() => {
  const hasAppliedFilters = (alarmStore.getListingTableState.dynamicFilters || []).length > 0
  const hasDraftFilters = draftFilters.value.length > 0
  const hasDraftInput = draftValue.value.trim() !== ''
  return hasAppliedFilters || hasDraftFilters || hasDraftInput
})

function isDateDraftField(field: string): boolean {
  return isAlarmListingDateField(field) || getFieldConfig(field).valueType === 'date'
}

function resolveDraftFilterValue(field: string): string | number | boolean {
  const config = getFieldConfig(field)
  const trimmedValue = draftValue.value.trim()

  if (isDateDraftField(field)) {
    return normalizeAlarmListingFilterDateInput(trimmedValue) || trimmedValue
  }

  if (config.valueType === 'number') {
    return Number(trimmedValue)
  }

  return trimmedValue
}

const canAddDraftFilter = computed(() => {
  const rawValue = draftValue.value.trim()
  if (!rawValue) return false
  if (isDateDraftField(draftField.value)) {
    const normalized = normalizeAlarmListingFilterDateInput(rawValue)
    return isValidAlarmListingFilterDateValue(normalized || rawValue)
  }
  return true
})

const operatorLabels = computed<Record<FilterOperator, string>>(() => ({
  eq: t('alarm_listing.filters.operators.eq'),
  cont: t('alarm_listing.filters.operators.cont'),
  gteq: t('alarm_listing.filters.operators.gteq'),
  lteq: t('alarm_listing.filters.operators.lteq'),
  gt: t('alarm_listing.filters.operators.gt'),
  lt: t('alarm_listing.filters.operators.lt'),
}))

function getFieldConfig(field: string): IFilterFieldConfig {
  return filterFields.value.find((item) => item.key === field) || filterFields.value[0]
}

function getFieldLabel(fieldKey: string): string {
  return getFieldConfig(fieldKey).label
}

function formatChipValue(filter: IDynamicFilterItem): string {
  const valueType = resolveAlarmListingFilterValueType(filter.field, filter.valueType)
  if (valueType === 'date' || isAlarmListingDateField(filter.field)) {
    return formatAlarmListingFilterDateForChip(String(filter.value))
  }
  return String(filter.value)
}

function handleDraftFieldChange(field: string) {
  const config = getFieldConfig(field)
  draftField.value = config.key
  draftOperator.value = config.operators[0]
  draftValue.value = ''
}

function handleDraftValueInput(event: Event) {
  const raw = (event.target as HTMLInputElement).value
  if (isDateDraftField(draftField.value)) {
    draftValue.value = normalizeAlarmListingFilterDateInput(raw) || raw
    return
  }
  draftValue.value = raw
}

function addFilterRow() {
  if (!canAddDraftFilter.value) return
  const config = getFieldConfig(draftField.value)
  const newFilter: IDynamicFilterItem = {
    id: `${Date.now()}-${Math.random()}`,
    field: draftField.value,
    operator: draftOperator.value,
    value: resolveDraftFilterValue(draftField.value),
    valueType: isDateDraftField(draftField.value) ? 'date' : config.valueType,
  }
  draftFilters.value.push(newFilter)
  draftValue.value = ''
}

function removeFilterRow(id: string) {
  draftFilters.value = draftFilters.value.filter((f) => f.id !== id)
}

function handleClassificationStatusSelected(option: { id?: boolean }) {
  const status = typeof option?.id === 'boolean' ? option.id : null
  setCurrentClassificationStatusFilter(status)
}

function isValidAppliedFilter(filter: IDynamicFilterItem): boolean {
  if (!filter.field || !filter.operator || filter.value === '') return false
  const valueType = resolveAlarmListingFilterValueType(filter.field, filter.valueType)
  if (valueType === 'date' || isAlarmListingDateField(filter.field)) {
    return isValidAlarmListingFilterDateValue(String(filter.value))
  }
  return true
}

function persistDraftFiltersToStore() {
  const validFilters = draftFilters.value
    .filter((f) => isValidAppliedFilter(f))
    .map((f) => normalizeDraftFilter({ ...f }))
  draftFilters.value = validFilters
  alarmStore.setListingTableState({
    dynamicFilters: validFilters as IAlarmListingDynamicFilter[],
    page: 1,
  })
}

function applyFilters() {
  persistDraftFiltersToStore()
}

function clearFilters() {
  if (!canClearFilters.value) return
  draftFilters.value = []
  draftValue.value = ''
  alarmStore.setListingTableState({
    dynamicFilters: [],
    page: 1,
  })
}
</script>

<template>
  <CollapsibleSearchPanel :title="t('alarm_listing.panel.title')">
    <div :class="['flex flex-col gap-4 p-4', panelControlClass]">
      <div class="border-b border-stroke pb-4">
        <div class="grid grid-cols-12 gap-3">
          <SearchBarField
            :label="t('alarm_listing.header.actions.search.fields.classification_status_filter')"
            class="col-span-12 min-w-0 md:col-span-6 lg:col-span-4">
            <Dropdown
              name="classifications"
              :options="listClassifications"
              label-attribute="classifications"
              :has-all-option="true"
              class="w-full"
              @option-selected="handleClassificationStatusSelected" />
          </SearchBarField>
        </div>
      </div>

      <div class="flex flex-col gap-3">
        <h2 class="font-satoshi text-sm font-semibold text-black">
          {{ t('alarm_listing.panel.dynamic_filters_label') }}
        </h2>

        <div
          class="grid w-full min-w-0 grid-cols-1 items-end gap-3 sm:grid-cols-2 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.85fr)_minmax(0,1.3fr)_auto]">
          <SearchBarField :label="t('alarm_listing.panel.field_label')" class="min-w-0">
            <select
              :value="draftField"
              :class="fieldInputClass"
              @change="handleDraftFieldChange(($event.target as HTMLSelectElement).value)">
              <option v-for="field in filterFields" :key="field.key" :value="field.key">
                {{ field.label }}
              </option>
            </select>
          </SearchBarField>

          <SearchBarField :label="t('alarm_listing.panel.operator_label')" class="min-w-0">
            <select v-model="draftOperator" :class="fieldInputClass">
              <option v-for="operator in getFieldConfig(draftField).operators" :key="operator" :value="operator">
                {{ operatorLabels[operator] }}
              </option>
            </select>
          </SearchBarField>

          <SearchBarField :label="t('alarm_listing.panel.value_label')" class="min-w-0">
            <input
              :key="draftField"
              :value="draftValue"
              :type="draftValueType === 'number' ? 'number' : draftValueType === 'date' ? 'datetime-local' : 'text'"
              :class="fieldInputClass"
              :placeholder="t('alarm_listing.panel.value_placeholder')"
              @input="handleDraftValueInput"
              @change="handleDraftValueInput" />
          </SearchBarField>

          <div class="flex min-w-0 items-end justify-end sm:col-span-2 xl:col-span-1">
            <button
              type="button"
              class="shrink-0 rounded-none border border-stroke bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="!canAddDraftFilter"
              @click="addFilterRow">
              {{ t('alarm_listing.panel.add_filter') }}
            </button>
          </div>
        </div>

        <div class="min-w-0" :class="draftFilters.length ? 'flex flex-wrap gap-2 pt-1' : ''">
          <div
            v-for="filter in draftFilters"
            :key="filter.id"
            class="flex items-center gap-2 rounded-none border border-stroke bg-gray-2 px-2 py-1.5 text-xs text-gray-700">
            <span class="font-semibold text-black">{{ getFieldLabel(filter.field) }}</span>
            <span class="text-gray-500">{{ operatorLabels[filter.operator as FilterOperator] ?? filter.operator }}</span>
            <span class="font-medium">{{ formatChipValue(filter) }}</span>
            <button
              type="button"
              class="flex h-5 w-5 items-center justify-center rounded-none border border-stroke bg-white text-gray-500 hover:bg-red hover:text-white"
              :aria-label="t('alarm_listing.panel.remove_filter')"
              @click="removeFilterRow(filter.id)">
              <XMarkIcon class="h-3 w-3" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-end gap-2 border-t border-stroke pt-4">
        <button
          type="button"
          class="rounded-none border border-stroke bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-2 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!canClearFilters"
          @click="clearFilters">
          {{ t('alarm_listing.panel.clear') }}
        </button>
        <button
          type="button"
          class="rounded-none border border-primary bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-2 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="!canApplyFilters"
          @click="applyFilters">
          {{ t('alarm_listing.panel.apply') }}
        </button>
      </div>
    </div>
  </CollapsibleSearchPanel>
</template>
